//src/App.tsx
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppLayout } from "./components/layout/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";

import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import RisksPage from "./pages/cadastros/RisksPage";
import RiskFormPage from "./pages/cadastros/RiskFormPage";
import AreasPage from "./pages/riscos/AreasPage";
import InspectionsPage from "./pages/InspectionsPage";
import DocumentsPage from "./pages/DocumentsPage";
import DocumentsFormPage from "./pages/DocumentsFormPage";
import TrainingsPage from "./pages/TrainingsPage";
import ReportsPage from "./pages/ReportsPage";
import AiHistoryReportPage from "./pages/AiHistoryReportPage";
import NotificationsPage from "./pages/NotificationsPage";
import UsersPage from "./pages/cadastros/UsersPage";
import UserFormPage from "./pages/cadastros/UserFormPage";
import GroupsPage from "./pages/cadastros/GroupsPage";
import UnitsPage from "./pages/cadastros/UnitsPage";
import UnitFormPage from "./pages/cadastros/UnitFormPage";
import SubscriptionPage from "./pages/financeiro/SubscriptionPage";
import InvoicesPage from "./pages/financeiro/InvoicesPage";
import PaymentsPage from "./pages/financeiro/PaymentsPage";
import NfsePage from "./pages/financeiro/NfsePage";
import SettingsPage from "./pages/SettingsPage";
import NotFound from "./pages/NotFound";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import TenantsPage from "./pages/cadastros/TenantsPage";
import TenantFormPage from "./pages/cadastros/TenantFormPage";
import TenantOnboardingPage from "./pages/cadastros/TenantOnboardingPage";
import DepartmentsPage from "./pages/cadastros/DepartmentsPage";
import DepartmentFormPage from "./pages/cadastros/DepartmentFormPage";
import JobPositionsPage from "./pages/cadastros/JobPositionsPage";
import JobPositionFormPage from "./pages/cadastros/JobPositionFormPage";
import GroSettingsPage from "./pages/gro/GroSettingsPage";
import WorkerGroupsPage from "./pages/gro/WorkerGroupsPage";
import WorkerGroupFormPage from "./pages/gro/WorkerGroupFormPage";
import QuestionnairesPage from "./pages/gro/QuestionnairesPage";
import QuestionnaireFormPage from "./pages/gro/QuestionnaireFormPage";
import QuestionnaireDetailPage from "./pages/gro/QuestionnaireDetailPage";
import SurveyCampaignsPage from "./pages/gro/SurveyCampaignsPage";
import SurveyCampaignFormPage from "./pages/gro/SurveyCampaignFormPage";
import SurveyCampaignDetailPage from "./pages/gro/SurveyCampaignDetailPage";
import PublicSurveyPage from "./pages/public/PublicSurveyPage";
import SurveyResultsPage from "./pages/gro/SurveyResultsPage";
import SurveyRiskSuggestionsPage from "./pages/gro/SurveyRiskSuggestionsPage";
import DimensionRiskRulesPage from "./pages/gro/DimensionRiskRulesPage";
import DimensionRiskRuleFormPage from "./pages/gro/DimensionRiskRuleFormPage";
import DimensionsPage from "./pages/gro/DimensionsPage";
import DimensionFormPage from "./pages/gro/DimensionFormPage";
import RiskInventoriesPage from "./pages/gro/RiskInventoriesPage";
import RiskInventoryFormPage from "./pages/gro/RiskInventoryFormPage";
import RiskInventoryDetailPage from "./pages/gro/RiskInventoryDetailPage";
import RiskInventoryItemActionPlansPage from "./pages/gro/RiskInventoryItemActionPlansPage";
import ActionPlanModelsPage from "./pages/gro/ActionPlanModelsPage";
import ActionPlanModelFormPage from "./pages/gro/ActionPlanModelFormPage";
import ActionPlanModelDetailPage from "./pages/gro/ActionPlanModelDetailPage";
import ActionPlanExecutionBoardPage from "./pages/gro/ActionPlanExecutionBoardPage";

const queryClient = new QueryClient();

function Protected({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

type RequirePermissionProps = {
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  children: React.ReactNode;
};

function RequirePermission({
  permission,
  permissions = [],
  requireAll = false,
  children,
}: RequirePermissionProps) {
  const location = useLocation();
  const { user, isAuthenticated, isLoading } = useAuth();

  const requiredPermissions = [
    ...(permission ? [permission] : []),
    ...permissions,
  ].filter(Boolean);

  if (isLoading) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requiredPermissions.length === 0) {
    return <>{children}</>;
  }

  const hasAll = requiredPermissions.every((p) => user?.permissions?.includes(p));
  const hasAny = requiredPermissions.some((p) => user?.permissions?.includes(p));
  const allowed = requireAll ? hasAll : hasAny;

  if (!allowed) {
    return <Navigate to="/403" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

function DocumentsRoutePage() {
  const { user } = useAuth();
  const canWriteDocuments = user?.permissions?.includes(PERMISSIONS.DOCUMENTS_WRITE) ?? false;

  if (canWriteDocuments) {
    return <DocumentsPage />;
  }

  return <DocumentsFormPage />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />

      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/403" element={<UnauthorizedPage />} />
        <Route path="/public/surveys/:token" element={<PublicSurveyPage />} />

        <Route
          path="/"
          element={
            <Protected>
              <AppLayout />
            </Protected>
          }
        >
          <Route
            index
            element={
              <RequirePermission permission={PERMISSIONS.DASHBOARD_READ}>
                <DashboardPage />
              </RequirePermission>
            }
          />

          <Route
            path="cadastros/empresa"
            element={
              <RequirePermission permissions={[PERMISSIONS.TENANT_READ, PERMISSIONS.PLATFORM_TENANTS_READ]}>
                <TenantsPage />
              </RequirePermission>
            }
          />

          <Route
            path="cadastros/empresa/novo"
            element={
              <RequirePermission permission={PERMISSIONS.PLATFORM_TENANTS_WRITE}>
                <TenantFormPage />
              </RequirePermission>
            }
          />

          <Route
            path="cadastros/empresa/:id"
            element={
              <RequirePermission permissions={[PERMISSIONS.TENANT_READ, PERMISSIONS.PLATFORM_TENANTS_READ]}>
                <TenantFormPage />
              </RequirePermission>
            }
          />

          <Route
            path="cadastros/empresa/:id/editar"
            element={
              <RequirePermission permission={PERMISSIONS.PLATFORM_TENANTS_WRITE}>
                <TenantFormPage />
              </RequirePermission>
            }
          />

          <Route
            path="cadastros/empresa/:id/onboarding"
            element={
              <RequirePermission permission={PERMISSIONS.PLATFORM_TENANTS_BOOTSTRAP}>
                <TenantOnboardingPage />
              </RequirePermission>
            }
          />

          <Route
            path="cadastros/unidades"
            element={
              <RequirePermission permission={PERMISSIONS.UNITS_READ}>
                <UnitsPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/unidades/novo"
            element={
              <RequirePermission permission={PERMISSIONS.UNITS_WRITE}>
                <UnitFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/unidades/:id"
            element={
              <RequirePermission permission={PERMISSIONS.UNITS_READ}>
                <UnitFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/unidades/:id/editar"
            element={
              <RequirePermission permission={PERMISSIONS.UNITS_WRITE}>
                <UnitFormPage />
              </RequirePermission>
            }
          />

          <Route
            path="cadastros/grupos"
            element={
              <RequirePermission permission={PERMISSIONS.GROUPS_READ}>
                <GroupsPage />
              </RequirePermission>
            }
          />

          <Route
            path="cadastros/usuarios"
            element={
              <RequirePermission permission={PERMISSIONS.USERS_READ}>
                <UsersPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/usuarios/novo"
            element={
              <RequirePermission permission={PERMISSIONS.USERS_WRITE}>
                <UserFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/usuarios/:id"
            element={
              <RequirePermission permission={PERMISSIONS.USERS_READ}>
                <UserFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/usuarios/:id/editar"
            element={
              <RequirePermission permission={PERMISSIONS.USERS_WRITE}>
                <UserFormPage />
              </RequirePermission>
            }
          />

          <Route
            path="cadastros/departamentos"
            element={
              <RequirePermission permission={PERMISSIONS.DEPARTMENTS_READ}>
                <DepartmentsPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/departamentos/novo"
            element={
              <RequirePermission permission={PERMISSIONS.DEPARTMENTS_WRITE}>
                <DepartmentFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/departamentos/:id"
            element={
              <RequirePermission permission={PERMISSIONS.DEPARTMENTS_READ}>
                <DepartmentFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/departamentos/:id/editar"
            element={
              <RequirePermission permission={PERMISSIONS.DEPARTMENTS_WRITE}>
                <DepartmentFormPage />
              </RequirePermission>
            }
          />

          <Route
            path="cadastros/cargos"
            element={
              <RequirePermission permission={PERMISSIONS.JOB_POSITIONS_READ}>
                <JobPositionsPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/cargos/novo"
            element={
              <RequirePermission permission={PERMISSIONS.JOB_POSITIONS_WRITE}>
                <JobPositionFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/cargos/:id"
            element={
              <RequirePermission permission={PERMISSIONS.JOB_POSITIONS_READ}>
                <JobPositionFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/cargos/:id/editar"
            element={
              <RequirePermission permission={PERMISSIONS.JOB_POSITIONS_WRITE}>
                <JobPositionFormPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/settings"
            element={
              <RequirePermission permission={PERMISSIONS.GRO_SETTINGS_READ}>
                <GroSettingsPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/worker-groups"
            element={
              <RequirePermission permission={PERMISSIONS.WORKER_GROUPS_READ}>
                <WorkerGroupsPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/worker-groups/novo"
            element={
              <RequirePermission permission={PERMISSIONS.WORKER_GROUPS_WRITE}>
                <WorkerGroupFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/worker-groups/:id"
            element={
              <RequirePermission permission={PERMISSIONS.WORKER_GROUPS_READ}>
                <WorkerGroupFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/worker-groups/:id/editar"
            element={
              <RequirePermission permission={PERMISSIONS.WORKER_GROUPS_WRITE}>
                <WorkerGroupFormPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/questionnaires"
            element={
              <RequirePermission permission={PERMISSIONS.QUESTIONNAIRES_READ}>
                <QuestionnairesPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/questionnaires/novo"
            element={
              <RequirePermission permission={PERMISSIONS.QUESTIONNAIRES_WRITE}>
                <QuestionnaireFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/questionnaires/:id"
            element={
              <RequirePermission permission={PERMISSIONS.QUESTIONNAIRES_READ}>
                <QuestionnaireDetailPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/dimensions"
            element={
              <RequirePermission permission={PERMISSIONS.QUESTIONNAIRES_READ}>
                <DimensionsPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/dimensions/new"
            element={
              <RequirePermission permission={PERMISSIONS.QUESTIONNAIRES_WRITE}>
                <DimensionFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/dimensions/:id"
            element={
              <RequirePermission permission={PERMISSIONS.QUESTIONNAIRES_READ}>
                <DimensionFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/dimensions/:id/editar"
            element={
              <RequirePermission permission={PERMISSIONS.QUESTIONNAIRES_WRITE}>
                <DimensionFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/questionnaires/:id/editar"
            element={
              <RequirePermission permission={PERMISSIONS.QUESTIONNAIRES_WRITE}>
                <QuestionnaireFormPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/campaigns"
            element={
              <RequirePermission permission={PERMISSIONS.SURVEY_CAMPAIGNS_READ}>
                <SurveyCampaignsPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/campaigns/novo"
            element={
              <RequirePermission permission={PERMISSIONS.SURVEY_CAMPAIGNS_WRITE}>
                <SurveyCampaignFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/campaigns/:id"
            element={
              <RequirePermission permission={PERMISSIONS.SURVEY_CAMPAIGNS_READ}>
                <SurveyCampaignDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/campaigns/:id/editar"
            element={
              <RequirePermission permission={PERMISSIONS.SURVEY_CAMPAIGNS_WRITE}>
                <SurveyCampaignFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/campaigns/:campaignId/resultados"
            element={
              <RequirePermission permission={PERMISSIONS.SURVEY_RESULTS_READ}>
                <SurveyResultsPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/dimension-risk-rules"
            element={
              <RequirePermission permission={PERMISSIONS.QUESTIONNAIRES_READ}>
                <DimensionRiskRulesPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/dimension-risk-rules/novo"
            element={
              <RequirePermission permission={PERMISSIONS.QUESTIONNAIRES_WRITE}>
                <DimensionRiskRuleFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/dimension-risk-rules/:id"
            element={
              <RequirePermission permission={PERMISSIONS.QUESTIONNAIRES_READ}>
                <DimensionRiskRuleFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/dimension-risk-rules/:id/editar"
            element={
              <RequirePermission permission={PERMISSIONS.QUESTIONNAIRES_WRITE}>
                <DimensionRiskRuleFormPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/risk-suggestions"
            element={
              <RequirePermission
                permissions={[
                  PERMISSIONS.SURVEY_RESULTS_READ,
                  PERMISSIONS.RISK_INVENTORY_WRITE,
                ]}
              >
                <SurveyRiskSuggestionsPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/risk-inventories"
            element={
              <RequirePermission permission={PERMISSIONS.RISK_INVENTORY_READ}>
                <RiskInventoriesPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/risk-inventories/novo"
            element={
              <RequirePermission permission={PERMISSIONS.RISK_INVENTORY_WRITE}>
                <RiskInventoryFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/risk-inventories/:id"
            element={
              <RequirePermission permission={PERMISSIONS.RISK_INVENTORY_READ}>
                <RiskInventoryDetailPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/risk-inventories/:inventoryId/items/:itemId/action-plans"
            element={
              <RequirePermission permission={PERMISSIONS.RISK_INVENTORY_READ}>
                <RiskInventoryItemActionPlansPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/resultados"
            element={<Navigate to="/gro/campaigns" replace />}
          />
          <Route
            path="gro/resultados/:campaignId"
            element={<Navigate to="/gro/campaigns/:campaignId/resultados" replace />}
          />
          <Route
            path="gro/results"
            element={<Navigate to="/gro/campaigns" replace />}
          />
          <Route
            path="gro/results/:campaignId"
            element={<Navigate to="/gro/campaigns/:campaignId/resultados" replace />}
          />
          <Route
            path="gro/inventarios"
            element={<Navigate to="/gro/risk-inventories" replace />}
          />


          <Route
            path="gro/action-plan-models"
            element={
              <RequirePermission permission={PERMISSIONS.ACTION_PLANS_READ}>
                <ActionPlanModelsPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/action-plan-models/novo"
            element={
              <RequirePermission permission={PERMISSIONS.ACTION_PLANS_WRITE}>
                <ActionPlanModelFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/action-plan-models/:id"
            element={
              <RequirePermission permission={PERMISSIONS.ACTION_PLANS_READ}>
                <ActionPlanModelDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/action-plan-models/:id/editar"
            element={
              <RequirePermission permission={PERMISSIONS.ACTION_PLANS_WRITE}>
                <ActionPlanModelFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/action-plans"
            element={
              <RequirePermission permission={PERMISSIONS.ACTION_PLANS_READ}>
                <ActionPlanModelsPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/action-plans/execution"
            element={
              <RequirePermission permission={PERMISSIONS.ACTION_PLANS_READ}>
                <ActionPlanExecutionBoardPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/action-plans/novo"
            element={
              <RequirePermission permission={PERMISSIONS.ACTION_PLANS_WRITE}>
                <ActionPlanModelFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="gro/action-plans/:id"
            element={
              <RequirePermission permission={PERMISSIONS.ACTION_PLANS_READ}>
                <ActionPlanModelDetailPage />
              </RequirePermission>
            }
          />

          <Route
            path="gro/action-plans/:id/editar"
            element={
              <RequirePermission permission={PERMISSIONS.ACTION_PLANS_WRITE}>
                <ActionPlanModelFormPage />
              </RequirePermission>
            }
          />

          <Route
            path="cadastros/riscos"
            element={
              <RequirePermission permission={PERMISSIONS.RISKS_READ}>
                <RisksPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/riscos/new"
            element={
              <RequirePermission permission={PERMISSIONS.RISKS_WRITE}>
                <RiskFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="cadastros/riscos/:id"
            element={
              <RequirePermission permission={PERMISSIONS.RISKS_WRITE}>
                <RiskFormPage />
              </RequirePermission>
            }
          />

          <Route
            path="riscos/areas"
            element={
              <RequirePermission permission={PERMISSIONS.AREAS_READ}>
                <AreasPage />
              </RequirePermission>
            }
          />

          <Route
            path="inspecoes"
            element={
              <RequirePermission permission={PERMISSIONS.INSPECTIONS_READ}>
                <InspectionsPage />
              </RequirePermission>
            }
          />

          <Route
            path="documentos"
            element={
              <RequirePermission permission={PERMISSIONS.DOCUMENTS_READ}>
                <DocumentsRoutePage />
              </RequirePermission>
            }
          />
          <Route
            path="treinamentos"
            element={
              <RequirePermission permission={PERMISSIONS.TRAININGS_READ}>
                <TrainingsPage />
              </RequirePermission>
            }
          />

          <Route
            path="relatorios"
            element={
              <RequirePermission permission={PERMISSIONS.REPORTS_READ}>
                <ReportsPage />
              </RequirePermission>
            }
          />
          <Route
            path="relatorios/historico-ai"
            element={
              <RequirePermission permission={PERMISSIONS.REPORTS_READ}>
                <AiHistoryReportPage />
              </RequirePermission>
            }
          />
          <Route
            path="notificacoes"
            element={
              <RequirePermission permission={PERMISSIONS.NOTIFICATIONS_READ}>
                <NotificationsPage />
              </RequirePermission>
            }
          />

          <Route
            path="financeiro/assinatura"
            element={
              <RequirePermission permissions={["billing.read"]}>
                <SubscriptionPage />
              </RequirePermission>
            }
          />
          <Route
            path="financeiro/faturas"
            element={
              <RequirePermission permissions={["billing.invoices.read"]}>
                <InvoicesPage />
              </RequirePermission>
            }
          />
          <Route
            path="financeiro/pagamentos"
            element={
              <RequirePermission permissions={["billing.read"]}>
                <PaymentsPage />
              </RequirePermission>
            }
          />
          <Route
            path="financeiro/nfse"
            element={
              <RequirePermission permissions={["billing.nf.write"]}>
                <NfsePage />
              </RequirePermission>
            }
          />

          <Route
            path="configuracoes"
            element={
              <Protected>
                <SettingsPage />
              </Protected>
            }
          />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
